import type { translations } from "../i18n";
import MapAnimation from "../components/landing/MapAnimation";
import { trackInitiateCheckout, trackSchedule } from "../lib/pixel";
import { buildRegisterUrl } from "../lib/tracking-url";

type T = (typeof translations)["de"]["bausignale"];

type Props = {
  navigate: (path: string) => void;
  t: T;
};

export default function BausignalePage({ navigate, t }: Props) {
  const handleTryFree = () => {
    trackInitiateCheckout({ source: "bausignale" });
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "cta_try_free", source: "bausignale" });
    }
  };

  const handleBookDemo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackSchedule({ source: "bausignale" });
    navigate("/demo");
  };

  return (
    <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-24 px-4 sm:px-6">
      <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-slate-950 to-slate-950 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid gap-10 lg:gap-16 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] items-center">
          <div className="text-left max-w-2xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.15] text-white mb-5 lg:mb-6">
              {t.titlePrefix}{" "}
              <span className="text-orange-500">{t.titleAccent}</span>
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed mb-8 lg:mb-10">
              {t.description}
            </p>

            <ul className="flex flex-col gap-3 mb-8 lg:mb-10">
              {t.bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-slate-200"
                >
                  <span className="text-orange-500 font-bold mt-0.5 flex-shrink-0">
                    ✓
                  </span>
                  <span className="text-sm sm:text-base lg:text-lg">{b}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start gap-4 sm:gap-5">
              <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                <a
                  href={buildRegisterUrl("https://app.neosearch.neobim.ai/start")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTryFree}
                  className="block text-center px-7 py-4 bg-orange-500 hover:bg-orange-600 text-white text-base lg:text-lg font-semibold rounded-xl transition-all hover:shadow-2xl hover:shadow-orange-500/40 hover:scale-[1.02] active:scale-95"
                >
                  {t.ctaPrimary}
                </a>
                <span className="text-xs text-slate-500 text-center sm:text-left">
                  {t.ctaPrimarySublabel}
                </span>
              </div>

              <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                <a
                  href="/demo"
                  onClick={handleBookDemo}
                  className="block text-center px-7 py-4 bg-transparent text-white text-base lg:text-lg font-semibold rounded-xl border border-slate-700 hover:border-orange-500/50 hover:bg-slate-900/40 transition-all active:scale-95"
                >
                  {t.ctaSecondary}
                </a>
                <span className="text-xs text-slate-500 text-center sm:text-left">
                  {t.ctaSecondarySublabel}
                </span>
              </div>
            </div>
          </div>

          <div className="relative w-full max-w-xl mx-auto lg:max-w-none">
            <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[5/6]">
              <MapAnimation />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800/70 mt-12 lg:mt-16 pt-8 lg:pt-10">
          <p className="text-xs sm:text-sm uppercase tracking-[0.14em] text-slate-400 mb-5 lg:mb-6 text-center">
            {t.trustHeading}
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
            {t.stats.map((s) => (
              <div
                key={s.value}
                className="flex flex-col items-center gap-1 text-center"
              >
                <strong className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-500">
                  {s.value}
                </strong>
                <span className="text-xs sm:text-sm text-slate-400 leading-snug whitespace-pre-line">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
